import {
  deleteAPI,
  getAPI,
  patchAPI,
  postAPI,
} from "../../../api/Networking";

export type ProductCommentAction = "created" | "updated" | "deleted";

export interface ProductCommentAttachment {
  id: number;
  file_path: string;
  file_name: string | null;
  uploaded_by: number;
  created_at: string;
}

export interface ProductComment {
  id: number;
  parent_id: number | null;
  body: string;
  created_by: number;
  author_name: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  can_edit: boolean;
  can_delete: boolean;
  attachments?: ProductCommentAttachment[];
  replies?: ProductComment[];
}

export interface ProductCommentHistoryEntry {
  id: number;
  comment_id: number;
  action: ProductCommentAction;
  body_before: string | null;
  body_after: string | null;
  actor_admin_id: number;
  actor_name: string | null;
  created_at: string;
}

const commentsBasePath = (projectId: number, productId: number) =>
  `/v1/projects/${projectId}/products/${productId}/comments`;

export default class ProjectProductCommentsAPI {
  listComments = async (
    projectId: number,
    productId: number,
  ): Promise<ProductComment[]> => {
    const response = await getAPI(commentsBasePath(projectId, productId));
    return response.result ?? [];
  };

  createComment = async (
    projectId: number,
    productId: number,
    body: string,
    attachments: Array<{ file_path: string; file_name?: string }> = [],
    parentId: number | null = null,
  ): Promise<ProductComment> => {
    const response = await postAPI(commentsBasePath(projectId, productId), {
      body,
      attachments,
      parent_id: parentId,
    });
    return response.result;
  };

  updateComment = async (
    projectId: number,
    productId: number,
    commentId: number,
    body: string,
  ): Promise<ProductComment> => {
    const response = await patchAPI(
      `${commentsBasePath(projectId, productId)}/${commentId}`,
      { body },
    );
    return response.result;
  };

  deleteComment = async (
    projectId: number,
    productId: number,
    commentId: number,
  ): Promise<ProductComment> => {
    const response = await deleteAPI(
      `${commentsBasePath(projectId, productId)}/${commentId}`,
    );
    return response.result;
  };

  getCommentHistory = async (
    projectId: number,
    productId: number,
    commentId: number,
  ): Promise<ProductCommentHistoryEntry[]> => {
    const response = await getAPI(
      `${commentsBasePath(projectId, productId)}/${commentId}/history`,
    );
    return response.result ?? [];
  };

  getUnreadCount = async (
    projectId: number,
    productId: number,
  ): Promise<number> => {
    const response = await getAPI(
      `${commentsBasePath(projectId, productId)}/unread-count`,
    );
    return Number(response.result?.unread_count || 0);
  };

  markAsRead = async (
    projectId: number,
    productId: number,
  ): Promise<{ last_read_at: string; unread_count: number }> => {
    const response = await postAPI(
      `${commentsBasePath(projectId, productId)}/mark-as-read`,
      {},
    );
    return (
      response.result ?? {
        last_read_at: new Date().toISOString(),
        unread_count: 0,
      }
    );
  };
}
